import api from './api';
import type { ByPospResponse } from './commissionReports';
import type { PospStatement } from '@/pages/commissions/pospLedgerStatement';
import type { ReconciliationSummary, StatementLine } from './reconciliation';
import type {
  CommissionCalculation,
  CommissionPayoutRecord,
  CommissionLedgerEntry,
  BrokerCommissionRateRecord,
  BrokerRateCsvUploadResult,
  CommissionSlabRecord,
  CommissionWaterfallRecord,
  PaginatedResponse,
  CommissionCalcRun,
} from '@/types';

/**
 * W0-A (B3). What `POST /commissions/calculations/recalculate/` did:
 * - `calculated`   first calculation for the policy;
 * - `recalculated` superseded and re-posted (nothing had been paid);
 * - `adjusted`     the commission was already PAID, so only the difference was
 *                  posted, as a linked clawback / adjustment (`recalc_adjustments`);
 * - `unchanged`    the result matched the current calculation, nothing posted;
 * - `concurrent`   another recalculation finished first; its result is returned.
 */
export type RecalcOutcome = 'calculated' | 'recalculated' | 'adjusted' | 'unchanged' | 'concurrent';

export interface RecalcAdjustment {
  party: 'posp' | 'broker';
  kind: 'clawback' | 'adjustment';
  /** Always positive; `kind` + the outcome sentence carry the direction. */
  amount: string;
  ledger_entry: string;
}

export type RecalculateCommissionResult = CommissionCalculation & {
  recalc_outcome?: RecalcOutcome;
  recalc_adjustments?: RecalcAdjustment[];
};

/**
 * W0-A decision 7. Every share of a calculation's `total_commission`, as the
 * list serializer serves it. `advertiser_commission` is carved out of the
 * broker's margin and is a real, additive share; `channel_payout_amount` is
 * owed OUT OF the broker share (it does not reduce `broker_commission`), so it
 * is shown as "of which", never added to the sum. Declared here while
 * `types/index.ts` is outside this workstream.
 */
export type CommissionCalculationWithParts = CommissionCalculation & {
  advertiser_commission?: string;
  advertiser_name?: string | null;
  channel_payout_amount?: string | null;
  channel_payout_name?: string | null;
};

/** B13: the payout list names the agent. */
export type CommissionPayoutWithPosp = CommissionPayoutRecord & {
  posp_name?: string | null;
};

/** B13: one policy inside a payout (`GET /commissions/payouts/{id}/lines/`). */
export interface PayoutLine {
  calculation: string;
  posp_name: string | null;
  policy_id: string | null;
  /** The insurer's policy number, falling back to ours. */
  policy_number: string | null;
  /** B-05: the insurer's alone -- blank means `policy_number` is ours. */
  insurer_policy_number?: string | null;
  holder_name: string | null;
  product_name: string | null;
  /** The product's insurance type, falling back to the policy's. */
  insurance_type: string | null;
  policy_date: string;
  posp_commission: string;
  /**
   * The share THIS payout carried for the policy: the broker's on a legacy
   * broker-entity payout, the POSP's otherwise.
   */
  commission_amount?: string | null;
  status: string;
}

export interface PayoutLinesResponse {
  payout: string;
  payout_reference: string;
  /** 'posp' for every new payout; 'broker' for the legacy broker-self payouts. */
  entity_type?: string;
  /** "POSP Commission" or "Broker Commission" — the column's own header. */
  commission_label?: string;
  posp_name: string | null;
  count: number;
  results: PayoutLine[];
}

/**
 * B12: the Reconciliation tab's filters. The keys ARE the API's query params
 * (apps/commissions/filters.py `InsurerStatementLineFilter`); both date bounds
 * are inclusive, and a single day is the same date in both.
 */
export interface ReconciliationFilters {
  status?: string;
  /** M-02: 'true' = only lines awaiting their policy; 'false' = all others. */
  awaiting_policy?: string;
  insurer?: string;
  statement_date_after?: string;
  statement_date_before?: string;
}

export type ReconciliationExportFormat = 'csv' | 'xlsx';

function reconciliationParams(filters?: ReconciliationFilters, extra?: Record<string, string>) {
  const params = new URLSearchParams(extra);
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
  }
  return params;
}

export const commissionService = {
  // Calculations
  async getCalculations(page = 1, filters?: Record<string, string>): Promise<PaginatedResponse<CommissionCalculationWithParts>> {
    const params = new URLSearchParams({ page: page.toString() });
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<PaginatedResponse<CommissionCalculationWithParts>>(`/commissions/calculations/?${params}`);
    return res.data;
  },

  /**
   * Queue #200/#221: starts a BACKGROUND run and returns its handle. The
   * endpoint answers 202 as soon as the run row exists -- it no longer waits
   * for the batch, which is what used to blow past the 30s client timeout on
   * any real book (and left the user staring at an error while the server
   * carried on succeeding). Poll `getCalculationRun` for progress.
   */
  async calculateCommissions(fromDate?: string, toDate?: string): Promise<CommissionCalcRun> {
    const res = await api.post<CommissionCalcRun>('/commissions/calculations/calculate/', {
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
    });
    return res.data;
  },

  /** Progress of a run; omit `id` for the caller's most recent one. */
  async getCalculationRun(id?: string): Promise<CommissionCalcRun> {
    const res = await api.get<CommissionCalcRun>(
      `/commissions/calculations/run/${id ? `?id=${encodeURIComponent(id)}` : ''}`
    );
    return res.data;
  },

  async approveCalculation(id: string): Promise<CommissionCalculation> {
    const res = await api.post<CommissionCalculation>(`/commissions/calculations/${id}/approve/`);
    return res.data;
  },

  async disputeCalculation(id: string): Promise<CommissionCalculation> {
    const res = await api.post<CommissionCalculation>(`/commissions/calculations/${id}/dispute/`);
    return res.data;
  },

  async reverseCalculation(id: string): Promise<CommissionCalculation> {
    const res = await api.post<CommissionCalculation>(`/commissions/calculations/${id}/reverse/`);
    return res.data;
  },

  // Recalculate the commission for a single policy (by id or policy number).
  // W0-A: the response says what the recalculation actually did -- see
  // RecalculateCommissionResult and describeRecalcOutcome (commissionRecalc.ts).
  async recalculateCommission(policy: string): Promise<RecalculateCommissionResult> {
    const res = await api.post<RecalculateCommissionResult>('/commissions/calculations/recalculate/', { policy });
    return res.data;
  },

  // Payouts
  async getPayouts(page = 1, filters?: Record<string, string>): Promise<PaginatedResponse<CommissionPayoutWithPosp>> {
    const params = new URLSearchParams({ page: page.toString() });
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<PaginatedResponse<CommissionPayoutWithPosp>>(`/commissions/payouts/?${params}`);
    return res.data;
  },

  /** B13: the policies one payout swept, POSP-wise. Tenant-scoped server-side (404 otherwise). */
  async getPayoutLines(id: string): Promise<PayoutLinesResponse> {
    const res = await api.get<PayoutLinesResponse>(`/commissions/payouts/${id}/lines/`);
    return res.data;
  },

  // Reconciliation (B12). The list, the summary and the download all take the
  // SAME filters, so the cards, the table and the file describe the same rows.
  async getReconciliationLines(page = 1, filters?: ReconciliationFilters): Promise<PaginatedResponse<StatementLine>> {
    const params = reconciliationParams(filters, { page: page.toString() });
    const res = await api.get<PaginatedResponse<StatementLine>>(`/commissions/statement-lines/?${params}`);
    return res.data;
  },

  async getReconciliationSummary(filters?: ReconciliationFilters): Promise<ReconciliationSummary> {
    const qs = reconciliationParams(filters).toString();
    const res = await api.get<ReconciliationSummary>(`/commissions/statement-lines/summary/${qs ? `?${qs}` : ''}`);
    return res.data;
  },

  /** Read-only download of exactly the filtered rows, as CSV or Excel. */
  async downloadReconciliation(format: ReconciliationExportFormat, filters?: ReconciliationFilters): Promise<Blob> {
    const params = reconciliationParams(filters, { format });
    const res = await api.get(`/commissions/statement-lines/export/?${params}`, { responseType: 'blob' });
    return res.data;
  },

  async createPayoutBatch(data: { entity_type: string; entity_id: string; start_date: string; end_date: string }): Promise<CommissionPayoutRecord> {
    const res = await api.post<CommissionPayoutRecord>('/commissions/payouts/create_batch/', data);
    return res.data;
  },

  async markProcessing(id: string): Promise<CommissionPayoutRecord> {
    const res = await api.post<CommissionPayoutRecord>(`/commissions/payouts/${id}/mark_processing/`);
    return res.data;
  },

  async markCompleted(
    id: string,
    payment: { paymentReference: string; paymentDate?: string; paymentMode?: string },
  ): Promise<CommissionPayoutRecord> {
    const res = await api.post<CommissionPayoutRecord>(`/commissions/payouts/${id}/mark_completed/`, {
      payment_reference: payment.paymentReference,
      payment_date: payment.paymentDate || undefined,
      payment_mode: payment.paymentMode || undefined,
    });
    return res.data;
  },

  async markFailed(id: string): Promise<CommissionPayoutRecord> {
    const res = await api.post<CommissionPayoutRecord>(`/commissions/payouts/${id}/mark_failed/`);
    return res.data;
  },

  // Ledger
  async getLedger(page = 1, filters?: Record<string, string>): Promise<PaginatedResponse<CommissionLedgerEntry>> {
    const params = new URLSearchParams({ page: page.toString() });
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<PaginatedResponse<CommissionLedgerEntry>>(`/commissions/ledger/?${params}`);
    return res.data;
  },

  async getRunningBalance(brokerId: string): Promise<{ balance: string }> {
    const res = await api.get<{ balance: string }>(`/commissions/ledger/running_balance/?broker_id=${brokerId}`);
    return res.data;
  },

  /** What ONE AGENT is still owed.
   *
   *  `running_balance` takes broker_id OR posp_id and answers a different
   *  number for each. Asking with the broker's id on an agent's page would
   *  return a plausible, wrong figure -- the worst kind, because nothing about
   *  it looks like an error. Separate method rather than an optional argument
   *  so the two cannot be confused at the call site. */
  async getPospRunningBalance(pospId: string): Promise<{ balance: string }> {
    const res = await api.get<{ balance: string }>(
      `/commissions/ledger/running_balance/?posp_id=${pospId}`
    );
    return res.data;
  },

  /** #692: one POSP's ledger statement for a financial year. */
  async getPospStatement(params: { posp_id?: string; financial_year?: string }): Promise<PospStatement> {
    const res = await api.get<PospStatement>('/commissions/ledger/statement/', { params });
    return res.data;
  },

  async downloadPospStatement(params: { posp_id?: string; financial_year?: string }, kind: 'csv' | 'pdf'): Promise<Blob> {
    const res = await api.get('/commissions/ledger/statement/', { params: { ...params, export: kind }, responseType: 'blob' });
    return res.data;
  },

  // Download the ledger as CSV honouring the active date/insurer/posp filters.
  async downloadLedger(filters?: Record<string, string>): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const qs = params.toString();
    const res = await api.get(`/commissions/ledger/export/${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
    return res.data;
  },

  /**
   * The Ledger tab's "By POSP" grouping — `GET /commissions/reports/by-posp/`
   * with BOTH of its filters (B8).
   *
   * Why this is not `commissionReportsService.getByPosp`: that service's
   * `ReportFilters` carries no `posp`, and its `buildParams` forwards only the
   * keys it knows. Handing it `{ posp }` would type-check at the call site,
   * drop the param on the floor and return the unfiltered book under a heading
   * that says it is filtered — a filter that reports success and filters
   * nothing. Folding `posp` into `ReportFilters` is the right long-term home
   * and is flagged for follow-up; `services/commissionReports.ts` is outside
   * this workstream's files.
   *
   * `insurer` accepts a UUID or an insurer code (the endpoint's own contract,
   * shared with by-insurer); the dropdown always sends an id.
   */
  async getByPospReport(
    page = 1,
    filters?: { period?: string; insurer?: string; posp?: string; date_from?: string; date_to?: string },
  ): Promise<ByPospResponse> {
    const params = new URLSearchParams({ page: page.toString() });
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<ByPospResponse>(`/commissions/reports/by-posp/?${params}`);
    return res.data;
  },

  // Insurance types mapped to an insurer (Add Rate dropdown)
  async getInsurerInsuranceTypes(insurerId: string): Promise<{ id: string; name: string; code: string }[]> {
    const res = await api.get<{ id: string; name: string; code: string }[]>(
      `/commissions/broker-rates/insurer-types/?insurer=${insurerId}`,
    );
    return res.data;
  },

  // Broker Rates
  async getBrokerRates(page = 1, filters?: Record<string, string>): Promise<PaginatedResponse<BrokerCommissionRateRecord>> {
    const params = new URLSearchParams({ page: page.toString() });
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<PaginatedResponse<BrokerCommissionRateRecord>>(`/commissions/broker-rates/?${params}`);
    return res.data;
  },

  async createBrokerRate(data: Partial<BrokerCommissionRateRecord>): Promise<BrokerCommissionRateRecord> {
    const res = await api.post<BrokerCommissionRateRecord>('/commissions/broker-rates/', data);
    return res.data;
  },

  async updateBrokerRate(id: string, data: Partial<BrokerCommissionRateRecord>): Promise<BrokerCommissionRateRecord> {
    const res = await api.patch<BrokerCommissionRateRecord>(`/commissions/broker-rates/${id}/`, data);
    return res.data;
  },

  async deleteBrokerRate(id: string): Promise<void> {
    await api.delete(`/commissions/broker-rates/${id}/`);
  },

  // Broker Rates — CSV (monthly grid). Pass insurerId to scope the template /
  // upload to a specific insurance company (pre-fills insurer, no GoDigit default).
  async downloadBrokerRateTemplate(insurerId?: string): Promise<Blob> {
    const qs = insurerId ? `?insurer=${insurerId}` : '';
    const res = await api.get(`/commissions/broker-rates/csv-template/${qs}`, { responseType: 'blob' });
    return res.data;
  },

  async uploadBrokerRatesCsv(file: File, insurerId?: string): Promise<BrokerRateCsvUploadResult> {
    const form = new FormData();
    form.append('file', file);
    if (insurerId) form.append('insurer', insurerId);
    const res = await api.post<BrokerRateCsvUploadResult>(
      '/commissions/broker-rates/upload-csv/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },

  // Commission Slabs
  async getSlabs(page = 1, filters?: Record<string, string>): Promise<PaginatedResponse<CommissionSlabRecord>> {
    const params = new URLSearchParams({ page: page.toString() });
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<PaginatedResponse<CommissionSlabRecord>>(`/commissions/slabs/?${params}`);
    return res.data;
  },

  async createSlab(data: Partial<CommissionSlabRecord>): Promise<CommissionSlabRecord> {
    const res = await api.post<CommissionSlabRecord>('/commissions/slabs/', data);
    return res.data;
  },

  async updateSlab(id: string, data: Partial<CommissionSlabRecord>): Promise<CommissionSlabRecord> {
    const res = await api.patch<CommissionSlabRecord>(`/commissions/slabs/${id}/`, data);
    return res.data;
  },

  async deleteSlab(id: string): Promise<void> {
    await api.delete(`/commissions/slabs/${id}/`);
  },

  // Commission Waterfalls (read-only for broker)
  async getWaterfalls(filters?: Record<string, string>): Promise<PaginatedResponse<CommissionWaterfallRecord>> {
    const params = new URLSearchParams();
    if (filters) Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const res = await api.get<PaginatedResponse<CommissionWaterfallRecord>>(`/commissions/waterfalls/?${params}`);
    return res.data;
  },
};
